/**
 * Blobo - Main Entry Point
 *
 * Initializes the Phaser game instance with mobile-optimized configuration.
 */

import Phaser from 'phaser';
import { inject } from '@vercel/analytics';
import { gameConfig } from '@/config/gameConfig';
import { BootScene } from '@/scenes/BootScene';
import { InstallPromptScene } from '@/scenes/InstallPromptScene';
import { MainMenuScene } from '@/scenes/MainMenuScene';
import { SettingsScene } from '@/scenes/SettingsScene';
import { ImprintScene } from '@/scenes/ImprintScene';
import { LevelSelectScene } from '@/scenes/LevelSelectScene';
import { LeaderboardScene } from '@/scenes/LeaderboardScene';
import { GameScene } from '@/scenes/GameScene';
import { UIScene } from '@/scenes/UIScene';
import { PauseScene } from '@/scenes/PauseScene';
import { VictoryScene } from '@/scenes/VictoryScene';
import { PWAManager } from '@/utils/PWAManager';
import { debugPWA } from '@/utils/pwa-debug';
import { AutoUpdateManager } from '@/utils/AutoUpdateManager';
import { SafeAreaUtil } from '@/utils/SafeAreaUtil';
import { GAME_EVENTS } from '@/config/constants';

// CRITICAL: Check for updates and clear cache BEFORE initializing anything
// This runs synchronously to block game initialization until check completes
await AutoUpdateManager.checkAndUpdate();

// Initialize Vercel Analytics
inject();

// Initialize PWA Manager (export for scenes to control visibility)
export const pwaManager = new PWAManager();

// Debug PWA (always run to help diagnose issues)
setTimeout(() => {
  pwaManager.logStatus();
  debugPWA();
}, 2000);

// Register all scenes
gameConfig.scene = [
  BootScene,
  InstallPromptScene,
  MainMenuScene,
  SettingsScene,
  ImprintScene,
  LevelSelectScene,
  LeaderboardScene,
  GameScene,
  UIScene,
  PauseScene,
  VictoryScene,
];

// Create game instance
const game = new Phaser.Game(gameConfig);

// Track current scene for back button handling
let currentScene: string = 'BootScene';

// Hide loading screen once game is ready
game.events.once('ready', () => {
  const loadingScreen = document.getElementById('loading-screen');
  if (loadingScreen) {
    loadingScreen.classList.add('hidden');
    setTimeout(() => {
      loadingScreen.remove();
    }, 500);
  }

  // Initialize history state for back button handling
  window.history.pushState({ scene: 'MainMenuScene' }, '', '');
});

/**
 * Get current device orientation using multiple detection methods
 * Uses Screen Orientation API (Android), window dimensions (iOS), and orientation angle (fallback)
 */
function getCurrentOrientation(): 'portrait' | 'landscape' {
  // Method 1: Screen Orientation API (best for Android)
  if (screen.orientation && screen.orientation.type) {
    const type = screen.orientation.type;
    if (type.includes('portrait')) return 'portrait';
    if (type.includes('landscape')) return 'landscape';
  }

  // Method 2: Screen orientation angle (fallback for older Android)
  if (typeof window.orientation !== 'undefined') {
    const angle = Math.abs(window.orientation as number);
    // 0 or 180 = portrait, 90 or 270 = landscape
    return angle === 90 || angle === 270 ? 'landscape' : 'portrait';
  }

  // Method 3: Window dimensions (iOS and universal fallback)
  return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
}

// Track orientation state
let lastOrientation: 'portrait' | 'landscape' = getCurrentOrientation();

/**
 * Handle orientation changes (iOS & Android)
 * Auto-pauses game when switching to landscape orientation
 * Ensures fullscreen mode is maintained in PWA
 */
function handleOrientationChange(): void {
  // Determine current orientation using multiple detection methods
  const currentOrientation = getCurrentOrientation();

  // Only proceed if orientation actually changed
  if (currentOrientation === lastOrientation) return;

  console.log(`📱 Orientation changed: ${lastOrientation} → ${currentOrientation}`);
  lastOrientation = currentOrientation;

  // Clear safe area cache to recalculate for new orientation
  SafeAreaUtil.clearCache();

  // Get the active GameScene if it exists
  const gameScene = game.scene.getScene('GameScene');

  if (!gameScene || !gameScene.scene.isActive()) {
    // Not in game, just refresh scale
    game.scale.refresh();
    return;
  }

  // If switching to landscape, auto-pause the game
  if (currentOrientation === 'landscape') {
    console.log('⚠️ Landscape orientation detected - auto-pausing game');
    // Emit pause event - GameScene will handle it
    game.events.emit(GAME_EVENTS.GAME_PAUSE);
  }

  // Refresh game scale for new orientation
  game.scale.refresh();

  // PWA fullscreen fix: Ensure app stays fullscreen when returning to portrait
  // Important for both iOS and Android PWAs
  if (pwaManager.isStandalone() && currentOrientation === 'portrait') {
    // Small delay to ensure orientation change completes
    setTimeout(() => {
      // Scroll to top to trigger fullscreen behavior
      window.scrollTo(0, 0);
      console.log('📱 PWA: Ensured fullscreen mode in portrait');
    }, 100);
  }
}

// Listen for orientation change events using multiple methods for cross-platform support

// Method 1: Screen Orientation API (modern Android & some iOS)
if (screen.orientation) {
  screen.orientation.addEventListener('change', () => {
    console.log('📱 Screen Orientation API event fired');
    handleOrientationChange();
  });
}

// Method 2: Legacy orientationchange event (iOS and older Android)
window.addEventListener('orientationchange', () => {
  console.log('📱 orientationchange event fired');
  handleOrientationChange();
});

// Method 3: Window resize as fallback
// Also handles responsive layout for all browsers
let resizeTimeout: number | undefined;
window.addEventListener('resize', () => {
  // Immediate scale refresh for responsive layout
  game.scale.refresh();

  // Debounced orientation change check (fallback)
  clearTimeout(resizeTimeout);
  resizeTimeout = window.setTimeout(() => {
    console.log('📱 resize event - checking orientation');
    handleOrientationChange();
  }, 150);
});

// Prevent context menu on long press (mobile)
window.addEventListener('contextmenu', e => {
  e.preventDefault();
});

// Handle mobile back button (Android/iOS)
window.addEventListener('popstate', event => {
  // Prevent default back navigation (closing app)
  event.preventDefault();

  // Get active scene
  const activeScene = game.scene.getScenes(true)[0];

  if (activeScene && activeScene.scene.key === 'GameScene') {
    // If in game, return to level select
    activeScene.scene.stop('UIScene');
    activeScene.scene.start('LevelSelectScene');

    // Restore level select state in history
    window.history.pushState({ scene: 'LevelSelectScene' }, '', '');
  } else if (activeScene && activeScene.scene.key === 'LevelSelectScene') {
    // If in level select, return to main menu
    activeScene.scene.start('MainMenuScene');

    // Restore menu state in history
    window.history.pushState({ scene: 'MainMenuScene' }, '', '');
  } else if (activeScene && activeScene.scene.key === 'SettingsScene') {
    // If in settings, return to main menu
    activeScene.scene.start('MainMenuScene');

    // Restore menu state in history
    window.history.pushState({ scene: 'MainMenuScene' }, '', '');
  } else if (activeScene && activeScene.scene.key === 'LeaderboardScene') {
    // If in leaderboard, return to main menu
    activeScene.scene.start('MainMenuScene');

    // Restore menu state in history
    window.history.pushState({ scene: 'MainMenuScene' }, '', '');
  } else if (activeScene && activeScene.scene.key === 'ImprintScene') {
    // If in imprint, return to main menu
    activeScene.scene.start('MainMenuScene');

    // Restore menu state in history
    window.history.pushState({ scene: 'MainMenuScene' }, '', '');
  } else {
    // If in menu, push state back to prevent closing
    window.history.pushState({ scene: 'MainMenuScene' }, '', '');
  }
});

// Track scene changes to manage history
game.events.on('scene-start', (scene: Phaser.Scene) => {
  currentScene = scene.scene.key;

  // Push history state when entering game
  if (currentScene === 'GameScene') {
    window.history.pushState({ scene: 'GameScene' }, '', '');
  }
});

// Export game instance for debugging (dev only)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - import.meta.env is provided by Vite
if (import.meta.env?.DEV) {
  (window as any).game = game;
  (window as any).pwa = pwaManager;
}

export default game;
